import { useEffect } from 'react';

import type { PartnerCreateEditContextValue, PartnerCreateEditPermissions } from 'pages/partner_create_edit/types';
import { NotificationContextProvider, useNotification } from 'app/notifications';
import { PartnerCreateEditContext } from 'pages/partner_create_edit/context';
import { getIdFromURL, getStringFromURL } from 'utils/other';
import { PageState } from 'pages/partner_create_edit/types';
import { Language, LanguagesApi } from 'apis/LanguagesApi';
import { setCountriesWithUnitedStateAtTop } from 'utils';
import { Outlet, useLocation } from 'react-router-dom';
import { Country, CountryApi } from 'apis/CountryApi';
import { DefaultLayout } from 'components/layout';
import { State, StateApi } from 'apis/StateApi';
import { useSuperState } from 'hooks';
import { useIntl } from 'react-intl';
import { useApp } from 'app';

export const PartnerCreateEdit = () => {

    const [state, setState] = useSuperState({
        countries: [] as Country[],
        languages: [] as Language[],
        states: [] as State[]
    });

    const { highestRole, selectedPartner } = useApp();
    const { catchWithToast } = useNotification();
    const { formatMessage } = useIntl();

    // Subscribe to location so changes to query params (e.g. PType, PageState)
    // from the sidebar links re-render this component and refresh context.
    useLocation();

    const partnerType = getIdFromURL('ptype')?.[0] ?? 4;
    const pageStateParam = getStringFromURL('pagestate');
    const pageState = pageStateParam?.toLowerCase() === 'edit' ? PageState.Edit : PageState.Create;

    const setStates = (newStates: State[]) => {

        setState({ states: newStates });

    };

    useEffect(() => {

        const loadData = async () => {

            try {

                const cou = await CountryApi.GetAll();
                setState({ countries: setCountriesWithUnitedStateAtTop(cou) });

            } catch (e) {

                catchWithToast(formatMessage({ id: 'failed_to_load_countries.6efc8543', defaultMessage: 'Failed to load countries.' }))(e);

            }

            try {

                const langs = await LanguagesApi.GetAll();
                setState({ languages: langs });

            } catch (e) {

                catchWithToast(formatMessage({ id: 'failed_to_load_languages.2aae1848', defaultMessage: 'Failed to load languages.' }))(e);

            }

            try {

                const val = await StateApi.GetAll(1);
                setState({ states: val });

            } catch (e) {

                catchWithToast(formatMessage({ id: 'failed_to_load_states.ec2a3c83', defaultMessage: 'Failed to load states.' }))(e);

            }

        };
        loadData();

    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    const permissions: PartnerCreateEditPermissions = {
        CanCreate: pageState === PageState.Create,
        CanEdit: pageState === PageState.Edit,
        IsAssociation: highestRole.id === 7,
        IsSuperAdmin: highestRole.id === 1
    };

    const context: PartnerCreateEditContextValue = {
        countries: state.countries,
        languages: state.languages,
        pageState,
        partnerId: selectedPartner.id,
        partnerType,
        permissions,
        selectedPartnerName: selectedPartner.name,
        setStates,
        states: state.states
    };

    return (
        <DefaultLayout>
            <PartnerCreateEditContext.Provider value={context}>
                <NotificationContextProvider>
                    <Outlet key={`${partnerType}-${pageState}`} />
                </NotificationContextProvider>
            </PartnerCreateEditContext.Provider>
        </DefaultLayout>

    );

};
